import React, { FC, lazy, Suspense } from 'react';
import { ToastContainer } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useRoutes } from 'react-router';

import useWindowSize from 'hooks/useWindowSize';
import { Stub } from 'components/pages';
import { MainLayout } from 'components/templates';
import { LOGIN } from 'queries';

import PATHS from './routes/PATHS';
import 'react-toastify/dist/ReactToastify.css';

const MessagePage = lazy(() => import('components/pages/Message'));
const HistoryPage = lazy(() => import('components/pages/History'));
const TranslationsPage = lazy(() => import('components/pages/Translations'));

const App: FC = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['auth'],
    queryFn: () => LOGIN(),
    refetchInterval: 300000,
  });

  const adminUser =
    data &&
    data.data &&
    'decoded_token' in data.data &&
    data.data.decoded_token &&
    typeof data.data.decoded_token === 'object' &&
    'groups' in data.data.decoded_token &&
    Array.isArray(data.data.decoded_token.groups) &&
    data.data.decoded_token.groups.includes('CODEX-MESSAGE-ADMIN');

  const [windowWidth, windowHeight] = useWindowSize();

  const page = useRoutes([
    {
      path: PATHS.ROOT,
      element: <MainLayout data={data} isLoading={isLoading} isAdmin={Boolean(adminUser)} />,
      children: [
        { index: true, element: <Navigate to={`${PATHS.MESSAGE}`} /> },
        {
          path: PATHS.MESSAGE,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<></>}>
                  <MessagePage />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: PATHS.HISTORY,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<></>}>
                  <HistoryPage />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: PATHS.TRANSLATIONS,
          children: [
            {
              index: true,
              element: adminUser ? (
                <Suspense fallback={<></>}>
                  <TranslationsPage />
                </Suspense>
              ) : (
                <Navigate to={`${PATHS.ROOT}`} />
              ),
            },
          ],
        },
        {
          path: '*',
          element: <Navigate to={`${PATHS.ROOT}`} />,
        },
      ],
    },
  ]);

  if (windowWidth <= 1020 || windowHeight <= 590) {
    return <Stub type={'mobile'} />;
  }

  return (
    <>
      {page}
      <ToastContainer />
    </>
  );
};

export default App;
